
import { useState, useEffect } from 'react';
import { useSEO, generateLocalBusinessSchema, generateWebPageSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface CartItem {
  id: string;
  serviceId: string;
  serviceName: string;
  address: string;
  price: number;
  lotSize: number;
  propertyData: any;
}

export default function InstantBookingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO Configuration
  useSEO({
    title: "Instant Lawn Care Booking Harford County MD - Buy & Book Services Online",
    description: "Purchase and instantly book lawn care services in Harford County, Maryland. Automatic pricing based on your property size. Pay online and get scheduled immediately. No waiting, no quotes needed.",
    keywords: "instant lawn care booking, buy lawn care online, automatic pricing, property size pricing, instant scheduling Maryland",
    canonical: "/instant-booking",
    ogImage: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema(),
        generateWebPageSchema(
          "Instant Lawn Care Booking Harford County MD - Buy & Book Services Online",
          "Purchase and instantly book lawn care services with automatic pricing based on your property size.",
          "/instant-booking"
        ),
        generateServiceSchema(
          "Instant Lawn Care Booking",
          "Purchase and instantly book lawn care services in Harford County, Maryland with automatic pricing based on property data and immediate scheduling."
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Instant Booking', url: '/instant-booking' }
        ])
      ]
    }
  });

  const [currentStep, setCurrentStep] = useState<'services' | 'address' | 'pricing' | 'booking' | 'payment'>('services');
  const [selectedService, setSelectedService] = useState<string>('');
  const [address, setAddress] = useState('');
  const [propertyData, setPropertyData] = useState<any>(null);
  const [pricing, setPricing] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);

  // Cart state
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);

  const services = [
    {
      id: 'lawn-mowing',
      name: 'Lawn Mowing Service',
      description: 'Professional weekly lawn mowing with edging and trimming',
      basePrice: 45,
      image: 'https://public.readdy.ai/ai/img_res/8910bbfc84d1d6d37876d4b22cd8fb19.jpg',
      features: ['Weekly mowing', 'Professional edging', 'String trimming', 'Cleanup included']
    },
    {
      id: 'mulching',
      name: 'Mulching Service',
      description: 'Premium mulch installation for garden beds and landscaping',
      basePrice: 120,
      image: 'https://readdy.ai/api/search-image?query=professional%20mulching%20service%20with%20rich%20dark%20mulch%20being%20spread%20around%20garden%20beds%20and%20plants%2C%20landscaping%20work%20in%20progress%2C%20clean%20professional%20appearance&width=600&height=400&seq=mulching-service&orientation=landscape',
      features: ['Premium mulch delivery', 'Professional installation', 'Bed preparation', 'Edge trimming']
    },
    {
      id: 'leaf-removal',
      name: 'Leaf Removal Service',
      description: 'Complete fall cleanup and leaf removal service',
      basePrice: 85,
      image: 'https://readdy.ai/api/search-image?query=professional%20leaf%20removal%20service%20with%20workers%20cleaning%20autumn%20leaves%20from%20residential%20property%2C%20fall%20cleanup%2C%20clean%20yard%20maintenance&width=600&height=400&seq=leaf-removal-service&orientation=landscape',
      features: ['Complete leaf removal', 'Gutter cleaning', 'Bed cleanup', 'Eco-friendly disposal']
    },
    {
      id: 'power-washing',
      name: 'Power Washing Service',
      description: 'Professional pressure washing for driveways, patios, and siding',
      basePrice: 150,
      image: 'https://readdy.ai/api/search-image?query=professional%20power%20washing%20service%20cleaning%20driveway%20and%20patio%2C%20high%20pressure%20water%20cleaning%2C%20before%20and%20after%20results%2C%20residential%20property%20maintenance&width=600&height=400&seq=power-washing-service&orientation=landscape',
      features: ['Driveway cleaning', 'Patio washing', 'Siding cleaning', 'Professional equipment']
    }
  ];

  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setCurrentStep('address');
  };

  const handleAddressSubmit = async () => {
    if (!address.trim()) return;
    
    setIsLoading(true);
    setCurrentStep('pricing');

    // Simulate property data lookup
    setTimeout(() => {
      // Mock property data - in real implementation, this would call a property data API
      const mockPropertyData = {
        lotSize: Math.floor(Math.random() * 15000) + 5000, // Random lot size between 5000-20000 sq ft
        propertyValue: Math.floor(Math.random() * 300000) + 200000,
        yearBuilt: Math.floor(Math.random() * 30) + 1990,
        bedrooms: Math.floor(Math.random() * 3) + 2,
        bathrooms: Math.floor(Math.random() * 2) + 2
      };

      const service = services.find(s => s.id === selectedService);
      const sizeMultiplier = mockPropertyData.lotSize / 10000; // Base pricing on 10,000 sq ft
      const finalPrice = Math.round(service!.basePrice * sizeMultiplier);

      setPropertyData(mockPropertyData);
      setPricing({
        basePrice: service!.basePrice,
        sizeMultiplier,
        finalPrice,
        lotSize: mockPropertyData.lotSize
      });
      setIsLoading(false);
    }, 2000);
  };

  const addToCart = () => {
    const service = services.find(s => s.id === selectedService);
    if (!service || !propertyData || !pricing) return;

    const cartItem: CartItem = {
      id: `${selectedService}-${address}-${Date.now()}`,
      serviceId: selectedService,
      serviceName: service.name,
      address,
      price: pricing.finalPrice,
      lotSize: pricing.lotSize,
      propertyData
    };

    setCartItems(prev => [...prev, cartItem]);
    
    // Reset form to add more services
    setCurrentStep('services');
    setSelectedService('');
    setAddress('');
    setPropertyData(null);
    setPricing(null);
  };

  const removeFromCart = (itemId: string) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCartItems([]);
    setShowCart(false);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price, 0);
  };

  const handleCheckoutCart = () => {
    setShowCart(false);
    setCurrentStep('payment');
    setBookingData({
      items: cartItems,
      totalPrice: getTotalPrice(),
      bookingId: `BK${Date.now()}`
    });
  };

  const handleBookService = () => {
    setCurrentStep('booking');
    // In real implementation, this would integrate with your booking system
    setBookingData({
      service: selectedService,
      address,
      price: pricing.finalPrice,
      bookingId: `BK${Date.now()}`
    });
  };

  const handlePayment = () => {
    setCurrentStep('payment');
    // In real implementation, this would integrate with Stripe
  };

  const qualifiedZipcodes = ['21001', '21005', '21009', '21010', '21014', '21015', '21017', '21028', '21034', '21040', '21047', '21050', '21051', '21078', '21082', '21084', '21085', '21087', '21111', '21120', '21128', '21131', '21132', '21154', '21156', '21160', '21161'];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Cart Button */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-[#83B328] text-white p-4 rounded-full shadow-2xl hover:bg-[#6d9620] transition-all z-50 cursor-pointer"
        >
          <div className="flex items-center space-x-2">
            <i className="ri-shopping-cart-line text-xl"></i>
            <span className="bg-white text-[#83B328] rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
              {cartItems.length}
            </span>
          </div>
        </button>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Your Cart</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {cartItems.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cartItems.map((item) => (
                      <div key={item.id} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-lg text-gray-900">{item.serviceName}</h3>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xl"></i>
                          </button>
                        </div>
                        <div className="space-y-2 text-sm text-gray-600 mb-3">
                          <div className="flex justify-between">
                            <span>Address:</span>
                            <span className="text-right">{item.address}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Lot Size:</span>
                            <span>{item.lotSize.toLocaleString()} sq ft</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-2xl font-bold text-[#83B328]">
                            ${item.price}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-6">
                    <div className="flex justify-between items-center mb-6">
                      <span className="text-2xl font-bold text-gray-900">Total:</span>
                      <span className="text-3xl font-bold text-[#83B328]">${getTotalPrice()}</span>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        onClick={clearCart}
                        className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Clear Cart
                      </button>
                      <button
                        onClick={handleCheckoutCart}
                        className="flex-1 bg-[#83B328] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#6d9620] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Checkout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-cover bg-center bg-no-repeat pt-24 pb-20" style={{
        backgroundImage: `linear-gradient(rgba(36, 130, 12, 0.85), rgba(131, 179, 40, 0.85)), url('https://readdy.ai/api/search-image?query=modern%20digital%20booking%20system%20with%20landscaping%20services%2C%20online%20purchasing%20interface%2C%20instant%20scheduling%20concept%2C%20professional%20landscaping%20background&width=1920&height=1080&seq=instant-booking-hero&orientation=landscape')`
      }}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Buy & Book Instantly
          </h1>
          <p className="text-2xl md:text-3xl text-white mb-10 max-w-4xl mx-auto font-light">
            Purchase lawn care services and get automatically scheduled, no quotes, no waiting
          </p>
        </div>
      </section>

      {/* Progress Indicator */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${currentStep === 'services' ? 'text-[#83B328]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 ${currentStep === 'services' ? 'border-[#83B328] bg-[#83B328]' : 'border-gray-300'} flex items-center justify-center mr-2`}>
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <span className="font-medium">Choose Service</span>
            </div>
            
            <div className={`flex items-center ${currentStep === 'address' || currentStep === 'pricing' || currentStep === 'booking' || currentStep === 'payment' ? 'text-[#83B328]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 ${currentStep === 'address' || currentStep === 'pricing' || currentStep === 'booking' || currentStep === 'payment' ? 'border-[#83B328] bg-[#83B328]' : 'border-gray-300'} flex items-center justify-center mr-2`}>
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <span className="font-medium">Property Info</span>
            </div>
            
            <div className={`flex items-center ${currentStep === 'pricing' || currentStep === 'booking' || currentStep === 'payment' ? 'text-[#83B328]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 ${currentStep === 'pricing' || currentStep === 'booking' || currentStep === 'payment' ? 'border-[#83B328] bg-[#83B328]' : 'border-gray-300'} flex items-center justify-center mr-2`}>
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <span className="font-medium">Pricing</span>
            </div>
            
            <div className={`flex items-center ${currentStep === 'booking' || currentStep === 'payment' ? 'text-[#83B328]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 ${currentStep === 'booking' || currentStep === 'payment' ? 'border-[#83B328] bg-[#83B328]' : 'border-gray-300'} flex items-center justify-center mr-2`}>
                <span className="text-white text-sm font-bold">4</span>
              </div>
              <span className="font-medium">Book & Pay</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Selection */}
      {currentStep === 'services' && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Choose Your Service
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Select the service you need and get instant pricing based on your property size
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all border border-gray-100 group cursor-pointer" onClick={() => handleServiceSelect(service.id)}>
                  <div className="h-60 w-full overflow-hidden bg-gray-100">
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="mb-6">
                      <div className="text-2xl font-bold text-[#83B328] mb-2">
                        Starting at ${service.basePrice}
                      </div>
                      <div className="text-sm text-gray-500">
                        Final price based on property size
                      </div>
                    </div>
                    <div className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <div className="w-5 h-5 bg-[#83B328] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <i className="ri-check-line text-white text-sm"></i>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <button className="w-full bg-[#83B328] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#6d9620] transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer text-lg shadow-lg">
                      Select This Service
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Address Input */}
      {currentStep === 'address' && (
        <section className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Enter Your Property Address
              </h2>
              <p className="text-xl text-gray-600">
                We'll use public property data to calculate your exact pricing
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="mb-6">
                <label className="block text-lg font-semibold text-gray-900 mb-3">
                  Property Address
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Enter your full address (e.g., 123 Main St, Bel Air, MD 21014)"
                  className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#83B328] focus:border-transparent text-lg"
                />
              </div>

              <div className="flex items-center mb-6 p-4 bg-blue-50 rounded-xl">
                <i className="ri-information-line text-2xl text-blue-600 mr-3"></i>
                <div className="text-blue-800">
                  <div className="font-semibold">How pricing works:</div>
                  <div className="text-sm">We use public property records to determine your lot size and calculate fair, accurate pricing instantly.</div>
                </div>
              </div>

              <button
                onClick={handleAddressSubmit}
                disabled={!address.trim()}
                className="w-full bg-[#83B328] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#6d9620] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-lg shadow-lg"
              >
                Get My Pricing
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Pricing Display */}
      {currentStep === 'pricing' && (
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {isLoading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#83B328] mx-auto mb-4"></div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Calculating Your Price...
                </h2>
                <p className="text-gray-600">
                  Looking up property data and calculating fair pricing based on your lot size
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">
                    Your Custom Pricing
                  </h2>
                  <p className="text-xl text-gray-600">
                    Based on your property's {pricing?.lotSize.toLocaleString()} sq ft lot size
                  </p>
                </div>

                <div className="bg-gradient-to-br from-[#83B328] to-[#6a9420] rounded-2xl p-8 text-white mb-8">
                  <div className="text-center">
                    <div className="text-6xl font-bold mb-4">
                      ${pricing?.finalPrice}
                    </div>
                    <div className="text-xl mb-6">
                      {services.find(s => s.id === selectedService)?.name}
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Pricing Breakdown</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Base Service Price</span>
                        <span>${pricing?.basePrice}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Property Size ({pricing?.lotSize.toLocaleString()} sq ft)</span>
                        <span>×{pricing?.sizeMultiplier.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-white/30 pt-3">
                        <div className="flex justify-between font-bold text-xl">
                          <span>Your Total Price</span>
                          <span>${pricing?.finalPrice}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-gray-900 mb-3">Property Details</h4>
                    <div className="space-y-2 text-gray-600">
                      <div>Lot Size: {propertyData?.lotSize.toLocaleString()} sq ft</div>
                      <div>Year Built: {propertyData?.yearBuilt}</div>
                      <div>Bedrooms: {propertyData?.bedrooms}</div>
                      <div>Bathrooms: {propertyData?.bathrooms}</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-lg text-gray-900 mb-3">What's Included</h4>
                    <div className="space-y-2">
                      {services.find(s => s.id === selectedService)?.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-gray-600">
                          <i className="ri-check-line text-[#83B328] mr-2"></i>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={addToCart}
                    className="flex-1 bg-gray-600 text-white px-6 py-4 rounded-xl font-bold hover:bg-gray-700 transition-all whitespace-nowrap cursor-pointer text-lg shadow-lg"
                  >
                    Add to Cart
                  </button>
                  <button
                    onClick={handleBookService}
                    className="flex-1 bg-[#83B328] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#6d9620] transition-all whitespace-nowrap cursor-pointer text-lg shadow-lg"
                  >
                    Book This Service for ${pricing?.finalPrice}
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Booking Confirmation */}
      {currentStep === 'booking' && (
        <section className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Ready to Book!
              </h2>
              <p className="text-xl text-gray-600">
                Complete your purchase and we'll schedule your service immediately
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="font-medium">Service</span>
                  <span>{services.find(s => s.id === selectedService)?.name}</span>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="font-medium">Address</span>
                  <span className="text-right">{address}</span>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="font-medium">Lot Size</span>
                  <span>{pricing?.lotSize.toLocaleString()} sq ft</span>
                </div>
                <div className="flex justify-between pt-3 border-t-2 border-gray-300">
                  <span className="text-2xl font-bold">Total</span>
                  <span className="text-2xl font-bold text-[#83B328]">${pricing?.finalPrice}</span>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <i className="ri-calendar-check-line text-2xl text-blue-600 mr-3 mt-1"></i>
                  <div>
                    <div className="font-semibold text-blue-900">Next Steps:</div>
                    <div className="text-blue-800 text-sm">After payment, we'll automatically schedule your service and send you confirmation details within 24 hours.</div>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePayment}
                className="w-full bg-[#83B328] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#6d9620] transition-all whitespace-nowrap cursor-pointer text-lg shadow-lg"
              >
                Proceed to Payment
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Payment */}
      {currentStep === 'payment' && (
        <section className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Complete Your Purchase
              </h2>
              <p className="text-xl text-gray-600">
                Secure payment processing
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="text-4xl font-bold text-[#83B328] mb-2">
                  {bookingData?.items ? `$${getTotalPrice()}` : `$${pricing?.finalPrice}`}
                </div>
                <div className="text-gray-600">
                  {bookingData?.items ? `${bookingData.items.length} Service${bookingData.items.length > 1 ? 's' : ''}` : services.find(s => s.id === selectedService)?.name}
                </div>
              </div>

              {bookingData?.items && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Services Included:</h3>
                  <div className="space-y-3">
                    {bookingData.items.map((item: CartItem) => (
                      <div key={item.id} className="bg-white rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-semibold text-gray-900">{item.serviceName}</div>
                            <div className="text-sm text-gray-600">{item.address}</div>
                          </div>
                          <div className="text-lg font-bold text-[#83B328]">${item.price}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center">
                <i className="ri-tools-line text-4xl text-yellow-600 mb-4"></i>
                <h3 className="text-xl font-bold text-yellow-800 mb-2">
                  Payment Integration Coming Soon
                </h3>
                <p className="text-yellow-700 mb-4">
                  This feature requires Stripe integration to process payments securely.
                </p>
                <button
                  onClick={() => {
                    const footer = document.querySelector('footer');
                    if (footer) {
                      footer.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="bg-yellow-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Contact Us to Complete Booking
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}